import type { Language } from "../data";
import type { DietFilter } from "../types";
import { fridgeIngredients } from "../data";

export const DAIRY_IDS = ["milk", "yogurt", "cheese", "cream", "butter", "ghee"];

// Gluten-bearing ingredient IDs: wheat/barley/rye and their derivatives.
// Includes flour, semolina, pasta, bread, bulgur, couscous, barley, rye,
// and kunafa dough (which is made from wheat semolina/vermicelli).
export const GLUTEN_IDS = [
  "flour", "semolina", "pasta", "bread", "bulgur", "couscous",
  "barley", "rye", "wheat", "kunafa_dough", "dough", "pastry",
];

// Set of all ingredient IDs that exist in the fridgeIngredients database.
// Used to guard the gluten-free filter: a recipe with an unknown ingredient
// ID cannot be confirmed gluten-free because the missing ingredient might
// contain gluten.
export const KNOWN_INGREDIENT_IDS: Set<string> = new Set(fridgeIngredients.map(i => i.id));

// Recipes whose ingredient list is incomplete or unverified must not
// qualify for gluten-free, since a missing gluten ingredient would
// silently pass the filter. This flag is set on recipes where the
// ingredient list is known to be incomplete (e.g. missing pastry).
export type RecipeCompleteness = "verified" | "unverified";

export const DIET_FILTERS: { id: DietFilter; icon: string; label: Record<Language, string>; passes: (recipe: import("../data").Recipe) => boolean }[] = [
  { id: "lowCalories",      icon: "🔥", label: { ar: "قليل السعرات",    en: "Low-Calorie"        }, passes: r => r.nutrition.calories < 300 },
  { id: "lowFat",           icon: "🥑", label: { ar: "أقل دسم",         en: "Low Fat"           }, passes: r => r.nutrition.fat < 8 },
  { id: "lowCarbs",         icon: "🌾", label: { ar: "أقل كربوهيدرات",  en: "Low Carbs"         }, passes: r => r.nutrition.carbs < 20 },
  { id: "lowSugar",         icon: "🍬", label: { ar: "أقل سكريات",      en: "Low Sugar"         }, passes: r => typeof r.nutrition.sugar === "number" && r.nutrition.sugar >= 0 && r.nutrition.sugar < 15 },
  { id: "highProtein",      icon: "💪", label: { ar: "عالي البروتين",   en: "High Protein"      }, passes: r => r.nutrition.protein > 15 },
  { id: "lowSodium",        icon: "🧂", label: { ar: "قليل الصوديوم",   en: "Low Sodium"        }, passes: r => r.nutrition.sodium < 400 },
  { id: "highFiber",        icon: "🌿", label: { ar: "غني بالألياف",    en: "High Fiber"        }, passes: r => r.nutrition.fiber > 5 },
  { id: "dietFriendly",     icon: "⚖️", label: { ar: "مناسب الرجيم",    en: "Diet-Friendly"     }, passes: r => r.nutrition.calories < 350 && r.nutrition.fat < 10 },
  { id: "hyperFriendly",    icon: "🫀", label: { ar: "مناسب الضغط",     en: "For Blood Pressure"}, passes: r => r.nutrition.sodium < 500 },
  { id: "diabetesFriendly", icon: "🩸", label: { ar: "مناسب السكري",    en: "For Diabetes"      }, passes: r => typeof r.nutrition.sugar === "number" && r.nutrition.sugar >= 0 && r.nutrition.sugar < 10 && r.nutrition.carbs < 25 },
  { id: "lactoseFree",      icon: "🥛", label: { ar: "بدون لاكتوز",     en: "Lactose-Free"      }, passes: r => !DAIRY_IDS.some(id => r.requiredIngredients.includes(id)) },
  { id: "glutenFree",       icon: "🌾", label: { ar: "بدون جلوتين",      en: "Gluten-Free"       }, passes: r =>
    r.ingredientCompleteness === "verified" &&
    r.requiredIngredients.every(id => KNOWN_INGREDIENT_IDS.has(id)) &&
    r.optionalIngredients.every(id => KNOWN_INGREDIENT_IDS.has(id)) &&
    !GLUTEN_IDS.some(id => [...r.requiredIngredients, ...r.optionalIngredients].includes(id)) },
];
